<template lang="pug">
  #color-chart.hair-color-chart.container.text-center.xs-f-small.max-at-tweak
    .row
      .fixed-width-tweak-upper
        .xs-12
          .intro.xs-f-xlarge.xs-8.sm-12.xs-text-left.sm-text-center.max-at-tweak Choose From 50+ Shades

    //- mobile and tablet uses a compact version of the filters that shows one filter upfront and hides the rest behind a modal
    .row(v-if="compactFilters && !!filters.length")
      .fixed-width-tweak-upper
        .xs-12
          .compact-filters.xs-f-xsmall.max-at-tweak
            multi-select-dropdown.filter-item(v-model="filters[0].value" :options="filters[0].options" :title="filters[0].label")
            button.filters-button.mr-btn.btn-inverse.bold.xs-ml-75m.xs-f-xxsmall.max-at-tweak(type="button" ref="filtersButton" @click="showPortalModal({ portalName: 'hair-color-chart-filters'})") filters

    //- desktop shows all filters
    .row(v-else-if="!!filters.length")
      .fixed-width-tweak-upper
        .xs-12
          .all-filters.flex-box.center-major-axis.center-minor-axis.nowrap.md-mb-200m
              p.filter-label.bold.upper.xs-f-small.max-at-tweak filter by:

              template(v-for="filter in filters")
                multi-select-dropdown.filter-item(v-model="filter.value" :options="filter.options" :title="filter.label")

    .row
      .fixed-width-tweak-upper
        .xs-12
          .swatches.flex-box
            Swatch(v-for="(product, index) in products" :product="product" @add-to-compare="$emit('compare', product)" :key="index")
            .peanut

    //- the filters modal that accompanies compact filter
    portal(to="hair-color-chart-filters")
      .filter-modal
        button.btn-reset.btn-close.xs-f-large.max-at-tweak(type="button" @click="$emit('hideModal')")
          span.icon-x
          span.ada-tooltip close
        .title.text-center.bold.upper.xs-mb-150m filters
        .all-filters.flex-box.center-minor-axis.md-mb-200m
          .filter-wrap.xs-12(v-for="filter, index in filters" :key="index")
            p.filter-title.bold.full-width.xs-f-small.md-f-medium.max-at-tweak.xs-mb-50m.xs-pl-15pct {{ filter.label }}
            multi-select-buttons.full-width.modal-buttons.xs-mb-100m(v-model="filter.value" :options="filter.options")

        .cta-wrap.flex-box.xs-12.nowrap
          button.mr-btn.btn-inverse.xs-f-xsmall.max-at-tweak.xs-6.xs-mr-75m(type="button" @click="clearAllFilters") clear all
          button.mr-btn.xs-f-xsmall.max-at-tweak.xs-6(type="button" @click="hideModal") done

</template>

<script>
  import { mapState, mapActions } from 'vuex';
  import { applyMatchTypeSpecialCharacters } from '@utilities/mrVueUtils';
  import Swatch from './Swatch';
  import MultiSelectButtons from "../../MultiSelectButtons";

  export default {
    name: "HairColorChart",
    emits: ['compare', 'hideModal'],

    data() {
      return {
        filters: [  // convert to cms once Object field type is available
          {
            value: [],
            accessor: 'color_characteristics.color_family',
            label: 'Hair Color',
            matchType: 'exact match',
            options: [
              { label: 'Black', value: 'black', disabled: false },
              { label: 'Brown', value: 'brown', disabled: false },
              { label: 'Red', value: 'red', disabled: false },
              { label: 'Blonde', value: 'blonde', disabled: false }
            ]
          },
          {
            value: [],
            accessor: 'color_characteristics.high_gray_coverage',
            label: 'Gray Coverage',
            matchType: 'exact match',
            options: [
              { label: 'For Resistant Grays', value: '1', disabled: false },
            ]
          },
          {
            value: [],
            accessor: 'color_characteristics.level',
            label: 'Level',
            matchType: 'exact match',
            options: [
              { label: 'Black', value: '2', disabled: false },
              { label: 'Brown Black', value: '3', disabled: false },
              { label: 'Darkest Brown', value: '4', disabled: false },
              { label: 'Dark Brown', value: '5', disabled: false },
              { label: 'Medium Brown', value: '6', disabled: false },
              { label: 'Light Brown', value: '7', disabled: false },
              { label: 'Dark Blonde', value: '8', disabled: false },
              { label: 'Medium Blonde', value: '9', disabled: false },
              { label: 'Light Blonde', value: '10', disabled: false },
              { label: 'Lightest Blonde', value: '11', disabled: false }
            ]
          },
          {
            value: [],
            accessor: 'code',
            label: 'Tone',
            matchType: 'partial match',
            options: [
              { label: 'Golden', value: 'G', disabled: false },
              { label: 'Auburn', value: 'M', disabled: false },
              { label: 'Copper', value: 'C', disabled: false},
              { label: 'Red', value: 'R', disabled: false },
              { label: 'Smokey', value: 'A', disabled: false },
              { label: 'Neutral', value: 'N', disabled: false }
            ]
          }
        ]
      };
    },

    components: {
      MultiSelectButtons,
      Swatch
    },

    inject: ['getObjProperty'],

    computed: {
      ...mapState('productCatalog', ['color_kit']),
      ...mapState('productCatalog', ['color_kit_loaded']),

      /**
       * returns an array of products that are active(status) and fulfills the user's filter criteria
       */
      products() {
        return this.color_kit.filter((d) => {
          if (d.status !== 1) {
            return false;
          }

          let hide = false;
          let i = 0;

          if (d.includesPurePigment && d.includesPurePigment != 'NONE') {
            hide = true;
          }

          while (!hide && i < this.filters.length) {
            const matchType = this.filters[i].matchType;
            const userSelections = this.filters[i].value;
            const productValue = this.getObjProperty(d, this.filters[i].accessor);

            let j = 0;
            while (j < userSelections.length) {
              const matchStr = applyMatchTypeSpecialCharacters(userSelections[j], matchType);

              if (new RegExp(matchStr).test(productValue) === true) {
                break;
              }

              // productValue matches none of the user selections
              if (++j === userSelections.length) {
                hide = true;
              }
            }

            i++;
          }

          return !hide;
        });
      },

      compactFilters() {
        return this.$root.windowData.breakpoint !== 'desktop';
      }
    },

    methods: {
      ...mapActions('productCatalog', ['loadProductsForType']),
      ...mapActions('modal', ['showPortalModal', 'hideModal']),

      clearAllFilters() {
        this.filters.forEach((d) => d.value = []);
      }
    },

    watch: {
      products(newProducts) {
        this.filters.forEach(filter => {
          filter.options.forEach(option => {
            option.disabled = false;

            if (filter.value.indexOf(option.value) == -1) {
              option.disabled = true;

              for (var x = 0; x < newProducts.length; x++) {
                var productValue = this.getObjProperty(newProducts[x], filter.accessor);

                if (new RegExp(option.value).test(productValue)) {
                  option.disabled = false;
                  break;
                }
              }
            }
          });
        });
      }
    },

    created() {
      if (!this.color_kit_loaded) {
        this.loadProductsForType('color_kit');
      }
    }
  };
</script>

<style scoped lang="stylus">
  .hair-color-chart
    .intro
    .swatches
    .compact-filters
      padding 0 6%

    .swatches
      .peanut
        flex-grow 5
        flex-shrink 1
        flex-basis 0

    .compact-filters
      display flex
      flex-direction row
      align-items stretch

    .filters-button
      padding 0 2em

    :deep(.multi-select-dropdown)
      width 14.5em
      margin 0

      .filter-item
        width 14.5em0

      .toggle-button
        padding 0 0.8em
        height 3em

      .dropdown-wrap
        position relative

        .options
          position absolute
          border 0.8px solid #979797

          .option
            label
              padding-right unset

    .all-filters
      .filter-title
        text-align left
        padding-left 3%
        margin-bottom 0.5em

      .filter-item
        width 14.5em

      :deep(.multi-select-buttons)
        margin-bottom 2em
        display flex
        align-item stretch

        .multi-select-button
          padding 0 0
          height 3.5em

      .cta-wrap
        position fixed
        left 0
        right 0
        bottom 0
        padding 1em 4%
        background-color color-fog-light
        border-top 1px solid color-fog

        .mr-btn
          width 48%
          max-width 16em

          &:first-child
            margin-right 4%

  @media mq-desktop-md-plus
    .all-filters
      .filter-label
        white-space nowrap
        letter-spacing 0.001em

      :deep(.multi-select-dropdown)
        margin-left 1.5em

        .dropdown-wrap
          position relative

          .options
            position absolute
            border 0.8px solid #979797

            .option
              label
                padding-right unset

</style>